import { isAnswerComplete } from "@pathwright/blocks-core/src/blocks/question/type/completion"
import {
  BLOCK_INPUT_STATES,
  useBlockCompletion
} from "@pathwright/blocks-engine"
import { QuillEditor } from "@pathwright/blocks/src/shared"
import { sansSerifStyles } from "@pathwright/blocks/src/ui/typography"
import TextArea from "@pathwright/ui/src/components/form/form-textarea/TextArea"
import { TOOLBAR_CONFIG_TYPES } from "@pathwright/ui/src/components/quill/toolbar/QuillToolbar"
import { useEffect } from "react"
import styled from "styled-components"
import { isFirefox } from "../../../utils"
import { getTextFromHTML } from "../utils"

const ParagraphQuestionViewWrapper = styled.div`
  .ParagraphQuestionView__rich-text-editor .ql-editor {
    min-height: 75px;
  }

  // Placeholder should match the positioning of the cursor
  .ql-editor.ql-blank:before {
    left: 5px;
  }

  @media screen and (max-width: 480px) {
    .blocks-paragraph__textarea div:first-of-type {
      align-items: stretch;
    }
  }

  .ParagraphQuestionView__rich-text-editor .ql-editor,
  .ParagraphQuestionView__rich-text-editor .ql-editor > p,
  .ParagraphQuestionView__rich-text-editor .ql-editor.ql-blank:before,
  .blocks-paragraph__textarea {
    ${sansSerifStyles({ size: 18 })}
  }
`

const StyledInput = styled.div`
  min-height: 87px;
  width: 100%;
  padding: 5px;
  margin-top: 5px;
  border: 1px solid #292e35;
  border-radius: 4px;
  background-color: transparent;

  /* Solves Safari printing bug */
  @media print {
    height: 0;
  }

  .ql-container {
    line-height: 25px;
    color: #292e35;
  }
`

const MIN_ANSWER_LENGTH = 1

const ParagraphQuestionView = ({ onChange, data, layout }) => {
  const {
    setUseAutocomplete,
    setBlockFunctions,
    preventChanges,
    getBlurProps
  } = useBlockCompletion()

  useEffect(() => {
    if (!data.feedback || !data.feedback.length) setUseAutocomplete(true)
    setBlockFunctions({
      validateInput,
      getBlockIsCompleted: (nextData) => isAnswerComplete(nextData, layout)
    })
  }, [])

  const validateInput = (data) => {
    const answer = data.answer
    const input = getTextFromHTML(getAnswerText(answer))

    if (!input) return BLOCK_INPUT_STATES.NONE

    const isValidInput = input && input.length >= MIN_ANSWER_LENGTH
    return isValidInput ? BLOCK_INPUT_STATES.VALID : BLOCK_INPUT_STATES.INVALID
  }

  // Handle edge/legacy answer text cases
  const getAnswerText = (answer) => {
    return answer && (!!answer.answerText || !!answer.answerHTML)
      ? answer.answerText
        ? answer.answerText // answerText is legacy plain text
        : answer.answerHTML === "undefined"
        ? ""
        : answer.answerHTML
      : ""
  }

  const hasLegacyAnswer = () => {
    return data.answer && data.answer.answerText
  }

  const handleChange = (text) => {
    const answerChange = hasLegacyAnswer()
      ? { answerText: text }
      : { answerHTML: text }

    const change = {
      answer: {
        ...data.answer,
        ...answerChange
      }
    }

    onChange(change, true)
  }

  return (
    <ParagraphQuestionViewWrapper>
      <div {...getBlurProps()}>
        {data.answer && !!data.answer.answerText ? (
          <TextArea
            className="blocks-paragraph__textarea"
            minRows={3}
            controlled={false}
            autocorrect="on"
            value={getAnswerText(data.answer)}
            onChange={handleChange}
            disabled={isFirefox() ? null : preventChanges}
            readOnly={isFirefox() ? preventChanges : null}
          />
        ) : (
          <StyledInput className="ParagraphQuestionView__rich-text-editor">
            <QuillEditor
              toolbar={TOOLBAR_CONFIG_TYPES.SIMPLE}
              html={getAnswerText(data.answer)} // need to pass html prop in case of a rerender
              onChange={handleChange}
              readOnly={preventChanges}
              contentType={null}
              // Controlled mode only when also readOnly;
              // Uncontrolled provides a smoother typing experience,
              // but need controlled mode for Review scenario
              controlled={!!preventChanges}
            />
          </StyledInput>
        )}
      </div>
    </ParagraphQuestionViewWrapper>
  )
}

export default ParagraphQuestionView
